import {
  Component,
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef
} from "@angular/cdk/overlay";
import { ComponentPortal, TemplatePortal } from "@angular/cdk/portal";

/**
 * PURPOSE: an embedded component used to display the tool tip
 */
@Component({
  selector: "app-tooltip-text-content-embedded",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="p-3 space-y-2">
      {{ textContent }}
    </div>
  `
})
class TooltipTextContentEmbeddedComponent {
  @Input() textContent: string = "";
}
// --- End Embedded TooltipTextContentComponent ---

/**
 * PURPOSE: a custom directive for displaying tool tips.
 * Implemented to resolve the issue with the Angular CDK where tooltips would not display after routing
 */
@Directive({
  selector: "[customCdkTooltip]",
  standalone: true
})
export class CustomCdkTooltipDirective implements OnDestroy {
  @Input("customCdkTooltip") tooltipContent!: string | TemplateRef<any>;
  @Input() customCdkTooltipPosition: "above" | "below" | "left" | "right" = "right";
  @Input() customCdkTooltipClass: string | string[] | undefined;
  @Input() customCdkTooltipShowDelay: number = 0;
  @Input() customCdkTooltipHideDelay: number = 200;

  private overlayRef: OverlayRef | null = null;
  private hideTimeoutId: any;
  private showTimeoutId: any;

  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly overlay: Overlay = inject(Overlay);
  private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);

  @HostListener("mouseenter")
  onMouseEnter(): void {
    this.clearHideTimeout();
    if (!this.overlayRef) {
      this.showTimeoutId = setTimeout(
        (): void => this.showTooltip(),
        this.customCdkTooltipShowDelay
      );
    }
  }

  @HostListener("mouseleave")
  onMouseLeave(): void {
    this.clearShowTimeout();
    this.hideTimeoutId = setTimeout(
      (): void => this.destroyTooltip(),
      this.customCdkTooltipHideDelay
    );
  }

  @HostListener("focus")
  onFocus(): void {
    this.clearHideTimeout();
    this.clearShowTimeout();
    this.showTooltip();
  }

  @HostListener("blur")
  onBlur(): void {
    this.destroyTooltip();
  }

  private showTooltip(): void {
    if (this.overlayRef) {
      return; // Already open
    }

    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        this.getPositionStrategyConfig(this.customCdkTooltipPosition),
        this.getPositionStrategyConfig(this.getFallbackPosition(this.customCdkTooltipPosition))
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: this.customCdkTooltipClass,
      disposeOnNavigation: true
    });

    if (typeof this.tooltipContent === "string") {
      const componentPortal = new ComponentPortal(
        TooltipTextContentEmbeddedComponent,
        this.viewContainerRef
      );
      const componentRef: ComponentRef<TooltipTextContentEmbeddedComponent> =
        this.overlayRef.attach(componentPortal);
      (componentRef.instance as TooltipTextContentEmbeddedComponent).textContent =
        this.tooltipContent;
    } else {
      const portal = new TemplatePortal(this.tooltipContent, this.viewContainerRef);
      this.overlayRef.attach(portal);
    }

    if (this.overlayRef.hasAttached()) {
      this.overlayRef.overlayElement.addEventListener(
        "mouseenter",
        this.onOverlayMouseEnter.bind(this)
      );
      this.overlayRef.overlayElement.addEventListener(
        "mouseleave",
        this.onOverlayMouseLeave.bind(this)
      );
    }
  }

  private onOverlayMouseEnter(): void {
    // If mouse enters the overlay, clear any pending hide operation
    this.clearHideTimeout();
  }

  private onOverlayMouseLeave(): void {
    // If mouse leaves the overlay, start hide timer
    this.hideTimeoutId = setTimeout(
      (): void => this.destroyTooltip(),
      this.customCdkTooltipHideDelay
    );
  }

  ngOnDestroy(): void {
    this.destroyTooltip();
    this.clearHideTimeout();
    this.clearShowTimeout();
  }

  private destroyTooltip(): void {
    if (this.overlayRef) {
      // Remove event listeners from the overlay element before disposing
      if (this.overlayRef.overlayElement) {
        this.overlayRef.overlayElement.removeEventListener(
          "mouseenter",
          this.onOverlayMouseEnter.bind(this)
        );
        this.overlayRef.overlayElement.removeEventListener(
          "mouseleave",
          this.onOverlayMouseLeave.bind(this)
        );
      }
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private clearHideTimeout(): void {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  private clearShowTimeout(): void {
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
  }

  private getPositionStrategyConfig(position: string): ConnectedPosition {
    switch (position) {
      case "above":
        return { originX: "center", originY: "top", overlayX: "center", overlayY: "bottom" };
      case "below":
        return { originX: "center", originY: "bottom", overlayX: "center", overlayY: "top" };
      case "left":
        return { originX: "start", originY: "center", overlayX: "end", overlayY: "center" };
      case "right":
        return { originX: "end", originY: "center", overlayX: "start", overlayY: "center" };
      default:
        return { originX: "center", originY: "bottom", overlayX: "center", overlayY: "top" };
    }
  }

  private getFallbackPosition(originalPosition: string): "above" | "below" | "left" | "right" {
    switch (originalPosition) {
      case "above":
        return "below";
      case "below":
        return "above";
      case "left":
        return "right";
      case "right":
        return "left";
      default:
        return "below";
    }
  }
}
